import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

// Define styles
const styles = StyleSheet.create({
  page: {
    paddingVertical: 20,
    paddingHorizontal: 20,
    position: "relative",
  },
  header: {
    textAlign: "center",
    marginBottom: 10,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
  },
  shopInfo: {
    fontSize: 10,
    marginBottom: 2,
  },
  reportTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginTop: 2,
  },
  thickDivider: {
    borderBottomWidth: 2,
    borderBottomColor: "#000",
    marginVertical: 0,
    width: "100%",
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    marginVertical: 1,
  },
  table: {
    display: "table",
    width: "100%",
    marginTop: 5, // Add margin to the top of the table
  },
  tableRow: {
    flexDirection: "row",
  },
  tableColHeader: {
    flex: 1,
    paddingVertical: 5,
    paddingLeft: 2,
    fontSize: 10,
    fontWeight: "bold",
    backgroundColor: "#E7EBF4",
  },
  tableColHeaderDebit: {
    textAlign: "center", // Center-align Debit column header
  },
  tableColHeaderCredit: {
    textAlign: "center", // Right-align Credit column header
    paddingRight: 5, // Add some padding for the right alignment
  },
  tableColHeaderBalance: {
    textAlign: "right", // Right-align Credit column header
    paddingRight: 5, // Add some padding for the right alignment
  },
  tableCol: {
    flex: 1,
    paddingVertical: 5,
    textAlign: "left", // Default alignment
    fontSize: 10,
    borderBottom: "1px solid #000",
  },
  // Adjusted flex values to fit all data properly in portrait mode
  tableColSerialNo: {
    flex: 0.3, // smaller for serial number
    textAlign: "left",
  },
  tableColTransactionDate: {
    flex: 1.2, // more width for date
  },
  tableColParticulars: {
    flex: 2, // more width for particulars
  },
  tableColPaymentTypes: {
    flex: 1, // moderate width
  },
  tableColVchTypes: {
    flex: 1, // moderate width
  },
  tableColVchNumber: {
    flex: 1, // moderate width for voucher number
  },
  tableColDebit: {
    flex: 1,
    textAlign: "center", // Center-align Debit column values
  },
  tableColCredit: {
    flex: 1,
    textAlign: "center", // Right-align Credit column values
    paddingRight: 5,
  },
  tableColBalance: {
    flex: 1.2, // a bit wider for balance values
    textAlign: "right", // Right-align Credit column values
    paddingRight: 5,
  },
  tableRowAlternate: {
    backgroundColor: "#F9F9F9",
  },
  totalRow: {
    backgroundColor: "#E7EBF4",
    fontWeight: "bold",
  },
  footer: {
    position: "absolute",
    bottom: 15,
    left: 0,
    right: 0,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 10,
    paddingHorizontal: 20,
  },
});

// PDF document component
const CashBookPortraitReport = ({
  reports,
  user,
  dateTo,
  dateFrom,
  openingBalance,
  currentDebit,
  currentCredit,
  closingBalance,
  paymentTypeName,
}) => {
  let dynamicBalance = openingBalance;
  return (
    <Document>
      <Page style={styles.page} size="A4" orientation="portrait">
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>
            {user?.invoice_settings?.shop_name || "Shop Name"}
          </Text>
          <Text style={styles.shopInfo}>{user?.address || "Address"}</Text>
          <Text style={styles.shopInfo}>Mobile: {user?.phone || "N/A"}</Text>
          <View style={styles.thickDivider} />
          <View style={styles.divider} />
          <View style={styles.thickDivider} />

          <Text style={styles.reportTitle}>
            Cash Book Detail Report ({paymentTypeName})
          </Text>
          <Text style={styles.reportTitle}>
            ({dateFrom} to {dateTo})
          </Text>
        </View>

        {/* Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableRow}>
            <Text style={[styles.tableColHeader, styles.tableColSerialNo]}>
              Sl.
            </Text>
            <Text
              style={[styles.tableColHeader, styles.tableColTransactionDate]}
            >
              Transaction Date
            </Text>
            <Text style={[styles.tableColHeader, styles.tableColParticulars]}>
              Particulars
            </Text>
            <Text style={[styles.tableColHeader, styles.tableColPaymentTypes]}>
              Payment Types
            </Text>
            <Text style={[styles.tableColHeader, styles.tableColVchTypes]}>
              Vch Types
            </Text>
            <Text style={[styles.tableColHeader, styles.tableColVchNumber]}>
              Vch No.
            </Text>
            <Text style={[styles.tableColHeader, styles.tableColDebit]}>
              Debit
            </Text>
            <Text style={[styles.tableColHeader, styles.tableColCredit]}>
              Credit
            </Text>
            <Text style={[styles.tableColHeader, styles.tableColBalance]}>
              Balance
            </Text>
          </View>

          {/* Table Body */}
          <View style={styles.tableRow}>
            <Text style={[styles.tableCol, styles.tableColSerialNo]}>1</Text>
            <Text
              style={[styles.tableCol, styles.tableColTransactionDate]}
            >
              {dateFrom}
            </Text>
            <Text style={[styles.tableCol, styles.tableColParticulars]}></Text>
            <Text style={[styles.tableCol, styles.tableColPaymentTypes]}></Text>
            <Text style={[styles.tableCol, styles.tableColVchTypes]}></Text>
            <Text style={[styles.tableCol, styles.tableColVchNumber]}></Text>
            <Text style={[styles.tableCol, styles.tableColDebit]}>
              Opening Balance
            </Text>
            <Text style={[styles.tableCol, styles.tableColCredit]}></Text>
            <Text style={[styles.tableCol, styles.tableColBalance]}>
              {Intl.NumberFormat("en-IN").format(openingBalance) || 0}
            </Text>
          </View>
          {reports.map((report, index) => (
            <View
              key={index}
              style={[
                styles.tableRow,
                index % 2 === 0 && styles.tableRowAlternate,
              ]}
            >
              <Text style={[styles.tableCol, styles.tableColSerialNo]}>
                {index + 2}
              </Text>
              <Text
                style={[styles.tableCol, styles.tableColTransactionDate]}
              >
                {report?.date || "N/A"}
              </Text>
              <Text
                style={[styles.tableCol, styles.tableColParticulars]}
              >
                {report?.particulars || "N/A"}
              </Text>
              <Text
                style={[styles.tableCol, styles.tableColPaymentTypes]}
              >
                {report?.type_name || "N/A"}
              </Text>
              <Text style={[styles.tableCol, styles.tableColVchTypes]}>
                {report?.type || "N/A"}
              </Text>
              <Text style={[styles.tableCol, styles.tableColVchNumber]}>
                {report?.invoice_id
                  ? report.invoice_id.split("-").pop()
                  : "N/A"}
              </Text>

              <Text style={[styles.tableCol, styles.tableColDebit]}>
                {report?.status.toLowerCase() == "out" ||
                report?.status.toLowerCase() == "debit"
                  ? Intl.NumberFormat("en-IN").format(report?.payment_amount)
                  : 0}
              </Text>
              <Text style={[styles.tableCol, styles.tableColCredit]}>
                {report?.status.toLowerCase() == "credit"
                  ? Intl.NumberFormat("en-IN").format(report?.payment_amount)
                  : 0}
              </Text>
              <Text style={[styles.tableCol, styles.tableColBalance]}>
                {report?.status.toLowerCase() == "out" ||
                report?.status.toLowerCase() == "debit"
                  ? Intl.NumberFormat("en-IN").format(
                      (dynamicBalance -= report?.payment_amount)
                    )
                  : Intl.NumberFormat("en-IN").format(
                      (dynamicBalance += report?.payment_amount)
                    )}
              </Text>
            </View>
          ))}

          {/* Totals */}
          <View style={[styles.tableRow, styles.totalRow]}>
            <Text style={[styles.tableCol, styles.tableColSerialNo]}></Text>
            <Text
              style={[styles.tableCol, styles.tableColTransactionDate]}
            ></Text>
            <Text style={[styles.tableCol, styles.tableColParticulars]}></Text>
            <Text style={[styles.tableCol, styles.tableColPaymentTypes]}></Text>
            <Text style={[styles.tableCol, styles.tableColVchTypes]}></Text>
            <Text style={[styles.tableCol, styles.tableColVchNumber]}>
              Current Total
            </Text>
            <Text style={[styles.tableCol, styles.tableColDebit]}>
              {Intl.NumberFormat("en-IN").format(currentDebit) || 0}
            </Text>
            <Text style={[styles.tableCol, styles.tableColCredit]}>
              {Intl.NumberFormat("en-IN").format(currentCredit) || 0}
            </Text>
            <Text style={[styles.tableCol, styles.tableColBalance]}></Text>
          </View>
          <View style={[styles.tableRow, styles.totalRow]}>
            <Text style={[styles.tableCol, styles.tableColSerialNo]}></Text>
            <Text
              style={[styles.tableCol, styles.tableColTransactionDate]}
            ></Text>
            <Text style={[styles.tableCol, styles.tableColParticulars]}></Text>
            <Text style={[styles.tableCol, styles.tableColPaymentTypes]}></Text>
            <Text style={[styles.tableCol, styles.tableColVchTypes]}></Text>
            <Text style={[styles.tableCol, styles.tableColVchNumber]}>
              Current Balance
            </Text>
            <Text style={[styles.tableCol, styles.tableColDebit]}>
              Credit - Debit
            </Text>
            <Text style={[styles.tableCol, styles.tableColCredit]}>
              {Intl.NumberFormat("en-IN").format(
                currentCredit - currentDebit
              ) || 0}
            </Text>
            <Text style={[styles.tableCol, styles.tableColBalance]}></Text>
          </View>
          <View style={[styles.tableRow, styles.totalRow]}>
            <Text style={[styles.tableCol, styles.tableColSerialNo]}></Text>
            <Text
              style={[styles.tableCol, styles.tableColTransactionDate]}
            ></Text>
            <Text style={[styles.tableCol, styles.tableColParticulars]}></Text>
            <Text style={[styles.tableCol, styles.tableColPaymentTypes]}></Text>
            <Text style={[styles.tableCol, styles.tableColVchTypes]}></Text>
            <Text style={[styles.tableCol, styles.tableColVchNumber]}>
              Closing Balance
            </Text>
            <Text style={[styles.tableCol, styles.tableColDebit]}></Text>
            <Text style={[styles.tableCol, styles.tableColCredit]}></Text>
            <Text style={[styles.tableCol, styles.tableColBalance]}>
              {Intl.NumberFormat("en-IN").format(closingBalance) || 0}
            </Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>Prepared by</Text>
          <Text>Checked/Recommended by</Text>
          <Text>Authorized by</Text>
        </View>
      </Page>
    </Document>
  );
};

export default CashBookPortraitReport;
